import { type ReactNode, useState } from 'react'
import clsx from 'clsx'
import {
  AlertOctagon, ChevronDown, ChevronUp, LayoutGrid, Loader2, Map as MapIcon, RotateCcw,
  Truck, Wrench, X,
} from 'lucide-react'
import {
  EmptyState, FilterChips, GeofenceCard, NetradyneCameraButton, SearchBox, SortBar, VehicleCard,
  type VehicleStatus,
} from '../cc'
import type { Alert, Actionable } from '../lib/types'
import { ArrivalBoard } from './ArrivalBoard'
import { BreakdownsPanel } from './BreakdownsPanel'
import { rewriteStopMessage } from '../lib/stopPlace'
import { useFleet } from '../state/store'
import { clearBreakdown } from '../lib/breakdownApi'
import { ago, clock } from '../lib/format'
import { TripReplayPicker, type RecentTrip } from './TripReplay'
import { VehicleDetailPage } from './vehicle/VehicleDetailPage'
import {
  detailGhostBtn,
  detailGridCard,
  detailHeader,
  detailPrimaryBtn,
  detailShell,
  reportCard,
  reportCardPad,
  reportCardSub,
  reportCardTitle,
  reportSegment,
  reportSegmentBtn,
} from './detailUi'

export interface FleetRow {
  vehicleId: string
  status: VehicleStatus
  statusLabel: string
  place: string
  load?: 'Loaded' | 'Unloaded' | null
  driver?: string | null
  lr?: string | null
  trailer?: string | null
  event?: string | null
  soc?: number | null
  speed: number | null
  progressPct: number | null
  elapsed: string
  exception: {
    icon?: 'clock' | 'zap' | 'power' | 'alert'
    text: string
    tone: 'warn' | 'danger' | 'info'
  } | null
}

export interface ZoneRow {
  id: string
  name: string
  type: string
  hubCode?: string | null
  inside: number
  speedLimitKph?: number | null
  tatMinutes?: number | null
  areaKm2?: number | null
}

export interface DetailWorkspaceProps {
  tabs: { id: string; label: string; count?: number }[]
  activeTab: string
  onTab: (id: string) => void
  onBackToMap: () => void
  /** When set, show vehicle telemetry section cards instead of tab grids. */
  vehicleFocusId: string | null
  onClearVehicleFocus: () => void

  // Fleet
  fleetRows: FleetRow[]
  fleetTotal: number
  fleetQuery: string
  onFleetQuery: (q: string) => void
  fleetFilter: string
  onFleetFilter: (id: string) => void
  fleetFilterOptions: { id: string; label: string; count?: number }[]
  fleetSort: string
  onFleetSort: (id: string) => void
  fleetSortOptions: { id: string; label: string }[]
  selectedVehicle: string | null
  selectedFence: string | null
  selectedFenceName: string | null
  onClearFence: () => void
  onSelectVehicle: (id: string) => void

  // Alerts
  alerts: Alert[]
  alertsEmptyTitle: string
  alertsEmptyBody: string
  alertElapsed: (a: Alert) => string
  onOpenAlert: (alertId: string) => void
  alertTone: (severity: string) => 'danger' | 'warn' | 'info'
  /** A KPI-card-driven severity filter (CommandCentre.tsx) is narrowing `alerts` below
   *  what the Alerts tab badge/count actually holds. Shows a "Critical only" clear chip
   *  instead of leaving this view a dead end once a filtered list is empty or capped. */
  alertsFilterActive?: boolean
  onClearAlertsFilter?: () => void
  /** A population-agnostic disclaimer (round-2 adversarial review: an earlier version
   *  made a specific "Showing N of M critical -- list is capped" numeric claim that was
   *  often the wrong explanation -- the KPI tile's own count and this list are drawn
   *  from different, not-directly-comparable populations; see CommandCentre.tsx's own
   *  comment on `alertsFilterCaveat` for which ones). Shown whenever the severity
   *  filter is active, not only when the two numbers happen to diverge. */
  alertsFilterCaveat?: string | null

  // Actionables
  actionables: Actionable[]
  actionableElapsed: (ts: number) => string
  onActionableClick: (item: Actionable) => void
  onMarkBreakdown: (vehicleId: string) => void
  canMarkSelected: boolean
  selectedPlate: string | null
  /** "Show on map" from a breakdown card -- switches back to map view and flies there.
   *  Distinct from onSelectVehicle above, which opens the full-page vehicle detail
   *  instead of the map. */
  onLocateVehicle?: (vehicleId: string, fallback?: { lat: number; lon: number }) => void

  // Zones
  zonesLoaded: boolean
  zoneRows: ZoneRow[]
  zoneQuery: string
  onZoneQuery: (q: string) => void
  zoneCounter: string
  showZoneTypes: boolean
  onToggleZoneTypes: () => void
  zoneType: string
  zoneTypeLabel: string | null
  zoneTypeChips: { id: string; label: string; count?: number }[]
  onZoneType: (id: string) => void
  zoneSummary: { occupied: number; trucks: number }
  fencesTotal: number
  onSelectZone: (id: string) => void

  // Trips
  tripActiveId: string | null
  onPickTrip: (t: RecentTrip) => void
  tripReplaySlot?: ReactNode
  /** Launch trip replay for a vehicle from full-page detail. */
  onReplayVehicleTrip?: (vehicleId: string) => void
  /** Launch breakdown replay (reported → next different corridor site) from the Breakdowns tab. */
  onReplayBreakdown?: (breakdownId: number) => void
}

/**
 * Full-page Command Centre browse mode: every SidePanel tab as a card grid,
 * with an optional vehicle-focus layer for telemetry section cards.
 */
export function DetailWorkspace(props: DetailWorkspaceProps): JSX.Element {
  const {
    tabs, activeTab, onTab, onBackToMap,
    vehicleFocusId, onClearVehicleFocus,
  } = props

  if (vehicleFocusId) {
    return (
      <VehicleDetailPage
        vehicleId={vehicleFocusId}
        onBackToMap={onBackToMap}
        onBackToBrowse={onClearVehicleFocus}
        onReplayTrip={props.onReplayVehicleTrip
          ? () => props.onReplayVehicleTrip!(vehicleFocusId)
          : undefined}
      />
    )
  }

  return (
    <div className={detailShell}>
      <header className={detailHeader}>
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          <LayoutGrid size={16} className="shrink-0 text-slate-400" />
          <div className="min-w-0">
            <h1 className={reportCardTitle}>Full page details</h1>
            <p className={reportCardSub}>Fleet, alerts, zones and trips</p>
          </div>
        </div>
        <div className={clsx(reportSegment, 'min-w-0 flex-1 flex-wrap')} role="tablist">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onTab(tab.id)}
                className={reportSegmentBtn(isActive)}
              >
                {tab.label}
                {tab.count != null ? (
                  <span className="ml-1.5 font-mono text-xs tabular-nums text-slate-400">{tab.count}</span>
                ) : null}
              </button>
            )
          })}
        </div>
        <button type="button" onClick={onBackToMap} className={detailPrimaryBtn}>
          <MapIcon size={12} />
          Back to map
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {activeTab === 'fleet' && <FleetGrid {...props} />}
        {activeTab === 'alerts' && <AlertsGrid {...props} />}
        {activeTab === 'actionables' && <ActionablesGrid {...props} />}
        {activeTab === 'zones' && <ZonesGrid {...props} />}
        {activeTab === 'trips' && <TripsPanel {...props} />}
        {activeTab === 'arrivals' && <ArrivalBoard />}
        {activeTab === 'breakdowns' && (
          <BreakdownsPanel
            onSelectVehicle={props.onLocateVehicle}
            onReplay={props.onReplayBreakdown}
          />
        )}
      </div>

      {props.tripReplaySlot}
    </div>
  )
}

function EmptyCard({ icon, title, body }: {
  icon?: ReactNode
  title: string
  body?: string
}): JSX.Element {
  return (
    <div className={clsx(reportCard, 'mx-auto max-w-md')}>
      <EmptyState icon={icon} title={title} body={body} />
    </div>
  )
}

function FenceBanner({ name, count, onClear }: {
  name: string
  count?: number
  onClear: () => void
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
      <span className="min-w-0 flex-1 truncate font-medium">
        {name}
        {count != null && (
          <span className="ml-1 font-mono tabular-nums text-slate-500">({count})</span>
        )}
      </span>
      <button
        type="button"
        onClick={onClear}
        className={clsx(detailGhostBtn, 'bg-white py-1')}
      >
        <X size={12} strokeWidth={2.4} />
        Show all
      </button>
    </div>
  )
}

function FleetGrid(p: DetailWorkspaceProps): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {p.selectedFence && (
        <FenceBanner
          name={`In ${p.selectedFenceName ?? p.selectedFence}`}
          count={p.fleetRows.length}
          onClear={p.onClearFence}
        />
      )}
      <div className={clsx(reportCard, 'flex flex-col gap-3 p-4')}>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="min-w-0 flex-1">
            <SearchBox
              value={p.fleetQuery}
              onChange={p.onFleetQuery}
              placeholder="Plate, driver, trip or zone…"
              counter={`${p.fleetRows.length}/${p.fleetTotal}`}
            />
          </div>
          <SortBar options={p.fleetSortOptions} active={p.fleetSort} onPick={p.onFleetSort} />
        </div>
        <FilterChips
          options={p.fleetFilterOptions}
          active={p.fleetFilter}
          onPick={p.onFleetFilter}
        />
      </div>
      {p.fleetRows.length === 0 ? (
        <EmptyCard
          icon={<Truck size={26} />}
          title="No vehicles match"
          body="Widen the filter, or clear the search."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {p.fleetRows.map((r) => (
            <div key={r.vehicleId} className="group relative">
              <VehicleCard
                plate={r.vehicleId}
                status={r.status}
                statusLabel={r.statusLabel}
                place={r.place}
                load={r.load}
                driver={r.driver}
                lr={r.lr}
                trailer={r.trailer}
                event={r.event}
                soc={r.soc}
                speed={r.speed}
                progressPct={r.progressPct}
                elapsed={r.elapsed}
                exception={r.exception}
                selected={p.selectedVehicle === r.vehicleId}
                onClick={() => p.onSelectVehicle(r.vehicleId)}
                className={detailGridCard}
              />
              <NetradyneCameraButton
                site="ultratech"
                vehicleNumber={r.vehicleId}
                className="absolute right-2 top-2 hidden group-hover:block"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Netradyne's own alert_id -- only present when sync_ultratech_netradyne.py wrote this
 *  row (alert.detail's source === 'netradyne', see that script's module docstring).
 *  Our own telemetry-derived alerts (HARSH_BRAKE etc.) have no Netradyne footage to
 *  request, so NetradyneCameraButton's Clip tab stays disabled for those. */
function netradyneAlertId(a: Alert): number | null {
  const detail = a.detail as { source?: string; netradyne_alert_id?: string | number } | null
  if (detail?.source !== 'netradyne' || detail.netradyne_alert_id == null) return null
  const id = Number(detail.netradyne_alert_id)
  return Number.isFinite(id) ? id : null
}

function AlertsGrid(p: DetailWorkspaceProps): JSX.Element {
  const vehicles = useFleet((s) => s.vehicles)
  const geofences = useFleet((s) => s.geofences)
  // The map-mode sidebar's own "Critical only" chip (CommandCentre.tsx) has an equivalent
  // here for the full-page layout -- without it, a KPI-card click that lands here with a
  // severity filter active has no visible explanation and no way back to "show everything"
  // except switching tabs away and back.
  const filterChip = p.alertsFilterActive && p.onClearAlertsFilter ? (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={p.onClearAlertsFilter}
        className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(220,38,38,0.3)]
                    bg-[#FEF2F2] px-2.5 py-1 text-[11px] font-medium text-[#B91C1C] transition-colors
                    hover:bg-[#FEE2E2]"
      >
        Critical only
        <X size={11} strokeWidth={2.4} />
      </button>
      {p.alertsFilterCaveat && (
        <span className="text-[10px] text-slate-400">{p.alertsFilterCaveat}</span>
      )}
    </div>
  ) : null
  if (p.alerts.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {filterChip}
        <EmptyCard title={p.alertsEmptyTitle} body={p.alertsEmptyBody} />
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      {filterChip}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {p.alerts.map((a) => (
          <div key={a.alert_id} className="group relative">
            <VehicleCard
              plate={a.vehicle_id ?? (a.cell ? `cell ${a.cell.slice(0, 8)}` : 'fleet')}
              status={a.severity === 'CRITICAL' ? 'alert' : 'idle'}
              statusLabel={a.kind.replace(/_/g, ' ').toLowerCase()}
              place={a.driver_name || '—'}
              speed={null}
              elapsed={p.alertElapsed(a)}
              exception={{
                text: rewriteStopMessage(
                  a.message,
                  (a.vehicle_id ? vehicles.get(a.vehicle_id)?.lat : undefined) ?? a.lat,
                  (a.vehicle_id ? vehicles.get(a.vehicle_id)?.lon : undefined) ?? a.lon,
                  geofences,
                ) + (a._groupCount ? ` · ${a._groupCount} events this leg` : ''),
                tone: p.alertTone(a.severity),
              }}
              onClick={() => p.onOpenAlert(a.alert_id)}
              className={detailGridCard}
            />
            {a.vehicle_id && (
              <NetradyneCameraButton
                site="ultratech"
                vehicleNumber={a.vehicle_id}
                alertId={netradyneAlertId(a)}
                className="absolute right-2 top-2 hidden group-hover:block"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ActionablesGrid(p: DetailWorkspaceProps): JSX.Element {
  const vehicles = useFleet((s) => s.vehicles)
  const geofences = useFleet((s) => s.geofences)
  return (
    <div className="flex flex-col gap-3">
      <div className={clsx(reportCard, 'flex items-center gap-2 px-4 py-3')}>
        <button
          type="button"
          disabled={!p.canMarkSelected}
          onClick={() => p.selectedPlate && p.onMarkBreakdown(p.selectedPlate)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-medium
                     text-rose-800 ring-1 ring-rose-200 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Wrench size={12} />
          Mark breakdown{p.selectedPlate ? ` · ${p.selectedPlate}` : ' · select vehicle'}
        </button>
      </div>
      {p.actionables.length === 0 ? (
        <EmptyCard
          icon={<AlertOctagon size={26} />}
          title="Nothing waiting"
          body="Open breakdowns and unresolved criticals appear here over SSE."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {p.actionables.map((item) => (
            <div key={item.id} className={clsx(reportCard, 'p-2')}>
              <VehicleCard
                plate={item.vehicle_id ?? (item.kind === 'alert' ? 'fleet' : '—')}
                status="alert"
                statusLabel={item.kind === 'breakdown'
                  ? `${item.severity} · down`
                  : item.title.replace(/_/g, ' ').toLowerCase()}
                place={rewriteStopMessage(
                  item.message || (item.eta_min != null ? `ETA ${item.eta_min} min` : '—'),
                  item.vehicle_id ? vehicles.get(item.vehicle_id)?.lat : null,
                  item.vehicle_id ? vehicles.get(item.vehicle_id)?.lon : null,
                  geofences,
                )}
                speed={null}
                elapsed={p.actionableElapsed(item.ts)}
                exception={{
                  icon: item.kind === 'breakdown' ? 'power' : 'alert',
                  text: item.kind === 'breakdown'
                    ? (item.title || 'Breakdown')
                    : rewriteStopMessage(
                      item.message || item.title,
                      item.vehicle_id ? vehicles.get(item.vehicle_id)?.lat : null,
                      item.vehicle_id ? vehicles.get(item.vehicle_id)?.lon : null,
                      geofences,
                    ),
                  tone: item.severity === 'immobilised' || item.severity === 'CRITICAL'
                    ? 'danger' : 'warn',
                }}
                onClick={() => p.onActionableClick(item)}
              />
              {item.kind === 'breakdown' && (
                <div className="mt-1 px-4 text-[10.5px] text-slate-500">
                  reported {ago(item.opened_ts ?? item.ts)} ago · {clock(item.opened_ts ?? item.ts)}
                </div>
              )}
              {item.vehicle_id && (
                <div className="mt-1 flex items-center gap-1 px-2">
                  <button
                    type="button"
                    onClick={() => p.onMarkBreakdown(item.vehicle_id!)}
                    className="flex-1 rounded-lg px-2 py-3 text-left text-xs font-medium
                               text-rose-800 hover:bg-rose-50"
                  >
                    {item.kind === 'breakdown' ? 'Update / clear breakdown…' : 'Mark vehicle breakdown…'}
                  </button>
                  {item.kind === 'breakdown' && (
                    <ActionableResolveButton vehicleId={item.vehicle_id}
                                              resumedMovingTs={item.resumed_moving_ts} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** One click, no form -- the resolve control the actionables row was missing. Amber when
 *  telemetry has already seen the vehicle moving again, so it reads as a confirmation
 *  rather than a blind guess. */
function ActionableResolveButton({
  vehicleId, resumedMovingTs,
}: { vehicleId: string; resumedMovingTs: number | null | undefined }): JSX.Element {
  const [busy, setBusy] = useState(false)
  const resumed = resumedMovingTs != null

  async function resolve(): Promise<void> {
    setBusy(true)
    try {
      await clearBreakdown(vehicleId)
    } catch {
      // The actionables list stays showing this item on failure -- retry is just a
      // second click, and the SSE broadcast is the only source of truth for "resolved".
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void resolve()}
      disabled={busy}
      title={resumed ? `started moving at ${clock(resumedMovingTs)}` : 'back in service'}
      className={clsx(
        'flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1.5 text-[11px] font-medium disabled:opacity-50',
        resumed
          ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
          : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
      )}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
      Resolve
    </button>
  )
}

function ZonesGrid(p: DetailWorkspaceProps): JSX.Element {
  if (!p.zonesLoaded) {
    return (
      <EmptyCard
        title="No geofences loaded"
        body="Run make geofences to parse the Earth project."
      />
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <div className={clsx(reportCard, 'flex flex-col gap-3 p-4')}>
        <SearchBox
          value={p.zoneQuery}
          onChange={p.onZoneQuery}
          placeholder="Search place, hub or type…"
          counter={p.zoneCounter}
        />
        <button
          type="button"
          onClick={p.onToggleZoneTypes}
          className="flex h-9 w-full items-center justify-between rounded-lg bg-slate-50 px-3 text-xs font-medium text-slate-700 ring-1 ring-slate-200 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
          aria-expanded={p.showZoneTypes}
        >
          <span>
            {p.showZoneTypes ? 'Hide types' : 'Show types'}
            <span className="ml-1.5 font-mono tabular-nums text-slate-400">
              {p.zoneType === 'all' ? p.fencesTotal : p.zoneTypeChips.find((c) => c.id === p.zoneType)?.count ?? 0}
            </span>
            {p.zoneTypeLabel && (
              <span className="ml-1.5 text-blue-600">· {p.zoneTypeLabel}</span>
            )}
          </span>
          {p.showZoneTypes
            ? <ChevronUp size={14} strokeWidth={2.2} className="text-slate-400" />
            : <ChevronDown size={14} strokeWidth={2.2} className="text-slate-400" />}
        </button>
        {p.showZoneTypes && (
          <FilterChips options={p.zoneTypeChips} active={p.zoneType} onPick={p.onZoneType} />
        )}
        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500">
          <span>
            <span className="font-semibold text-slate-900">{p.zoneSummary.occupied}</span>
            {' '}occupied
          </span>
          <span className="font-mono tabular-nums">
            <span className="font-semibold text-blue-600">{p.zoneSummary.trucks}</span>
            {' '}trucks inside
          </span>
        </div>
      </div>
      {p.selectedFence && (
        <FenceBanner
          name={`Showing vehicles in ${p.selectedFenceName ?? p.selectedFence}`}
          onClear={p.onClearFence}
        />
      )}
      {p.zoneRows.length === 0 ? (
        <EmptyCard title="No zones match" body="Widen the type filter, or clear the search." />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {p.zoneRows.map((z) => (
            <GeofenceCard
              key={z.id}
              name={z.name}
              type={z.type}
              hubCode={z.hubCode}
              inside={z.inside}
              speedLimitKph={z.speedLimitKph}
              tatMinutes={z.tatMinutes}
              areaKm2={z.areaKm2}
              selected={p.selectedFence === z.id}
              onClick={() => p.onSelectZone(z.id)}
              className={detailGridCard}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TripsPanel(p: DetailWorkspaceProps): JSX.Element {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      <div className={clsx(reportCard, reportCardPad)}>
        <h2 className={reportCardTitle}>Trip replay</h2>
        <p className={clsx(reportCardSub, 'mt-1 max-w-prose')}>
          Replay a leg from recorded geotags and halts. After you pick a trip, use Back to map
          to watch it on the corridor — the live scrubber stays under the map.
        </p>
        <div className="mt-4 divide-y divide-slate-200 overflow-hidden rounded-lg ring-1 ring-slate-200">
          <TripReplayPicker
            activeId={p.tripActiveId}
            onPick={p.onPickTrip}
          />
        </div>
      </div>
    </div>
  )
}
